import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import './HonorHall.css';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { MAIN_MENU_MUSIC_PATH } from './config/mainMenuMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import { useContainerScale } from './hooks/useViewportScale';
import { TutorialMessageBox, TutorialReplayButton, useMenuTutorialReplay } from './tutorial/index';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import ExploreUnknownMissionDepthDisplay from './components/ExploreUnknownMissionDepthDisplay';
import { isPlayer1DevUser } from './tutorial/isPlayer1DevUser';
import { buildHonorHallExplorersDevMockRows } from './utils/honorHallExplorersDevMock';

const HONOR_HALL_TAB_IDS = Object.freeze([
  'battle-legends',
  'battle-strategies',
  'war-heroes',
  'explorers',
  'asset-strategies',
]);

/**
 * @param {unknown} state
 * @returns {string | null}
 */
function parseHonorHallTabFromLocationState(state) {
  if (state == null || typeof state !== 'object') {
    return null;
  }
  const tab = state.honorHallTab;
  if (tab == null) {
    return null;
  }
  if (typeof tab !== 'string' || !HONOR_HALL_TAB_IDS.includes(tab)) {
    throw new Error(`HonorHall: invalid honorHallTab (${String(tab)})`);
  }
  return tab;
}

function HonorHall({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const initialTab = parseHonorHallTabFromLocationState(location.state) ?? 'battle-legends';
  const pendingShowUserRankRef = useRef(location.state?.honorHallShowUserRank === true);
  const appliedNavStateRef = useRef(false);
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  useMusic(MAIN_MENU_MUSIC_PATH);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [assetStrategiesData, setAssetStrategiesData] = useState([]);
  const [battleStrategiesData, setBattleStrategiesData] = useState([]);
  const [battleLegendsData, setBattleLegendsData] = useState([]);
  const [explorersData, setExplorersData] = useState([]);
  const [warHeroesData, setWarHeroesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showUserRank, setShowUserRank] = useState(false);
  const [showTutorialHonorHall, setShowTutorialHonorHall] = useState(false);
  const [explorersDevMockActive, setExplorersDevMockActive] = useState(false);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);
  const { isReplayRequest: isReplayingHonorHallTutorial } = useMenuTutorialReplay('honor_hall', {
    enabled: Boolean(user) && !loading,
    onReplay: () => setShowTutorialHonorHall(true),
  });
  
  const itemsPerPage = 100;
  const isPlayer1Dev = isPlayer1DevUser(user);

  const explorersDisplayData = useMemo(() => {
    if (explorersDevMockActive) {
      return buildHonorHallExplorersDevMockRows();
    }
    return explorersData;
  }, [explorersDevMockActive, explorersData]);

  // Determine if current tab shows players or strategies
  const isPlayerTab = activeTab === 'battle-legends' || activeTab === 'explorers' || activeTab === 'war-heroes';
  const isStrategyTab = !isPlayerTab;

  const userPlayerTabRank = useMemo(() => {
    if (!user?.username) return null;
    const name = user.username;
    if (activeTab === 'battle-legends') {
      const i = battleLegendsData.findIndex((p) => p.username === name);
      return i === -1 ? null : i + 1;
    }
    if (activeTab === 'explorers') {
      const i = explorersDisplayData.findIndex((p) => p.username === name);
      return i === -1 ? null : i + 1;
    }
    if (activeTab === 'war-heroes') {
      const i = warHeroesData.findIndex((p) => p.username === name);
      return i === -1 ? null : i + 1;
    }
    return null;
  }, [activeTab, user?.username, battleLegendsData, explorersDisplayData, warHeroesData]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (appliedNavStateRef.current) {
      return;
    }
    const tab = parseHonorHallTabFromLocationState(location.state);
    const showRank = location.state?.honorHallShowUserRank === true;
    if (!tab && !showRank) {
      return;
    }
    appliedNavStateRef.current = true;
    if (tab) {
      setActiveTab(tab);
      setCurrentPage(1);
      setSearchTerm('');
      setShowUserRank(false);
    }
    if (showRank) {
      pendingShowUserRankRef.current = true;
    }
  }, [location.state]);

  useEffect(() => {
    if (!loading && pendingShowUserRankRef.current && userPlayerTabRank) {
      const targetPage = Math.ceil(userPlayerTabRank / itemsPerPage);
      setCurrentPage(targetPage);
      setShowUserRank(true);
      pendingShowUserRankRef.current = false;
    }
  }, [loading, userPlayerTabRank]);

  useEffect(() => {
    if (user && !loading && shouldShowTutorial('honor_hall')) {
      const timer = setTimeout(() => setShowTutorialHonorHall(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, loading, shouldShowTutorial]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [assetStrategiesResponse, battleStrategiesResponse, battleLegendsResponse, explorersResponse, warHeroesResponse] = await Promise.all([
        fetch(`${API_URL}/honor-hall/asset-strategies`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/battle-strategies`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/battle-legends`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/explorers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/war-heroes`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (!assetStrategiesResponse.ok || !battleStrategiesResponse.ok || !battleLegendsResponse.ok || !explorersResponse.ok || !warHeroesResponse.ok) {
        throw new Error('Failed to fetch honor hall data');
      }

      const assetStrategies = await assetStrategiesResponse.json();
      const battleStrategies = await battleStrategiesResponse.json();
      const battleLegends = await battleLegendsResponse.json();
      const explorers = await explorersResponse.json();
      const warHeroes = await warHeroesResponse.json();

      // Ensure we have arrays
      setAssetStrategiesData(Array.isArray(assetStrategies) ? assetStrategies : []);
      setBattleStrategiesData(Array.isArray(battleStrategies) ? battleStrategies : []);
      setBattleLegendsData(Array.isArray(battleLegends) ? battleLegends : []);
      setExplorersData(Array.isArray(explorers) ? explorers : []);
      setWarHeroesData(Array.isArray(warHeroes) ? warHeroes : []);
    } catch (error) {
      console.error('Error fetching honor hall data:', error);
      setAssetStrategiesData([]);
      setBattleStrategiesData([]);
      setBattleLegendsData([]);
      setExplorersData([]);
      setWarHeroesData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (strategyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscribe-strategy/${strategyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchData();
      } else {
        const errorData = await response.json();
        alert(`${t('honorHall.subscribeFailed')} ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error subscribing to strategy:', error);
      alert(t('honorHall.subscribeFailed'));
    }
  };

  const handleUnsubscribe = async (strategyId) => {
    if (strategyId == null) {
      alert(t('honorHall.unsubscribeError'));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/unsubscribe-strategy/${strategyId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`${t('honorHall.unsubscribeFailed')}${errorData.error ? `: ${errorData.error}` : ''}`);
      }
    } catch (error) {
      console.error('Error unsubscribing from strategy:', error);
      alert(t('honorHall.unsubscribeFailed'));
    }
  };

  const renderStrategyAction = (strategyId, isOwned, isSubscribed) => {
    if (isOwned) {
      return <span className="owned">{t('honorHall.owned')}</span>;
    }
    if (isSubscribed) {
      return (
        <button
          type="button"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            handleUnsubscribe(strategyId);
          }}
          className="unsubscribe-button"
        >
          {t('honorHall.unsubscribe')}
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          handleSubscribe(strategyId);
        }}
        className="subscribe-button"
      >
        {t('honorHall.subscribe')}
      </button>
    );
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'asset-strategies':
        return assetStrategiesData;
      case 'battle-strategies':
        return battleStrategiesData;
      case 'battle-legends':
        return battleLegendsData;
      case 'explorers':
        return explorersDisplayData;
      case 'war-heroes':
        return warHeroesData;
      default:
        return [];
    }
  };

  const formatAverageMissionTime = (avgSeconds) => {
    if (avgSeconds == null || avgSeconds === 0) return '-';
    const totalSeconds = Math.round(Number(avgSeconds));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
    return parts.join(' ');
  };

  const filteredData = isPlayerTab
    ? (Array.isArray(getCurrentData()) ? getCurrentData() : []).filter((player) => {
        const q = searchTerm.toLowerCase();
        const nameMatch = player.username && player.username.toLowerCase().includes(q);
        if (activeTab === 'explorers') {
          const stratName = player.run_strategy_name;
          const stratMatch =
            typeof stratName === 'string' && stratName.toLowerCase().includes(q);
          return nameMatch || stratMatch;
        }
        return nameMatch;
      })
    : (Array.isArray(getCurrentData()) ? getCurrentData() : []).filter(strategy => 
        (strategy.strategy_name && strategy.strategy_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (strategy.player_name && strategy.player_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );


  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handleShowMe = () => {
    if (isPlayerTab && userPlayerTabRank) {
      const targetPage = Math.ceil(userPlayerTabRank / itemsPerPage);
      setCurrentPage(targetPage);
      setShowUserRank(true);
    }
  };

  if (loading) {
    return (
      <div className="honor-hall-viewport-wrapper">
        <div 
          className="honor-hall-fullscreen-background"
          style={{ backgroundImage: 'url(/images/honorhall.png)' }}
        />
        <div 
          className={`honor-hall-viewport-container${isContainerMobile ? ' mobile' : ''}`}
          style={{
            width: `${containerBaseWidth}px`,
            height: `${containerBaseHeight}px`,
            transformOrigin: '0 0',
            transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
          }}
        >
          <div className="honor-hall-container">
            <div className="loading">{t('honorHall.loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="honor-hall-viewport-wrapper">
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="honor-hall-fullscreen-background"
        style={{ backgroundImage: 'url(/images/honorhall.png)' }}
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`honor-hall-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="honor-hall-container">
      
          {user && (
            <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
              <FriendsIcon />
              <SettingsMenu onLogout={() => {
                playSoundEffect('/sounds/click.mp3');
                onLogout();
                navigate('/');
              }} />
              <span style={{ color: 'white', marginRight: '10px' }}>{user}</span>
            </div>
          )}

      <div className="honor-hall-header">
        <h1>{t('honorHall.title')}</h1>
        <button onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          navigate('/welcome');
        }} className="back-button">
          {t('common.back')}
        </button>
      </div>

      <div className="table-controls">
        <div>
          <input
            type="text"
            placeholder={isPlayerTab ? t('honorHall.searchPlayers') : t('honorHall.searchStrategies')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {isPlayerTab && userPlayerTabRank && (
            <button onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              handleShowMe();
            }} className="show-me-button">
              {t('honorHall.showMe')} ({t('honorHall.rank')} #{userPlayerTabRank})
            </button>
          )}
          {isPlayer1Dev && activeTab === 'explorers' && (
            <button
              type="button"
              className="honor-hall-explorers-dev-mock-btn"
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                setExplorersDevMockActive((active) => !active);
                setCurrentPage(1);
                setShowUserRank(false);
                setSearchTerm('');
              }}
            >
              {explorersDevMockActive
                ? t('honorHall.devMockExplorersClear')
                : t('honorHall.devMockExplorersButton')}
            </button>
          )}
        </div>
        
        <div className="tabs">
          <button 
            className={`tab-golden ${activeTab === 'battle-legends' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('battle-legends');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.battleLegends')}
          </button>
          <button 
            className={`tab-golden ${activeTab === 'battle-strategies' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('battle-strategies');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.battleStrategies')}
          </button>
          <button 
            className={`tab-war-heroes ${activeTab === 'war-heroes' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('war-heroes');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.warHeroes')}
          </button>
          <button 
            className={`tab-purple ${activeTab === 'explorers' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('explorers');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.explorers')}
          </button>
          <button 
            className={`tab-blue ${activeTab === 'asset-strategies' ? 'active' : ''}`}
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setActiveTab('asset-strategies');
              setCurrentPage(1);
              setSearchTerm('');
            }}
          >
            {t('honorHall.assetStrategies')}
          </button>
        </div>
      </div>

      <div
        className={`table-container${
          activeTab === 'war-heroes' ? ' war-heroes-table' : ''
        }${activeTab === 'explorers' ? ' explorers-table' : ''}${
          activeTab === 'battle-legends' ? ' battle-legends-table' : ''
        }${isStrategyTab ? ' strategy-table' : ''}`}
      >
        {activeTab === 'war-heroes' ? (
          <table className="honor-table war-heroes-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th>{t('honorHall.averageMissionTime')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((player, index) => (
                <tr 
                  key={player.username}
                  className={showUserRank && player.username === user?.username ? 'highlighted' : ''}
                >
                  <td>{startIndex + index + 1}</td>
                  <td>{player.username}</td>
                  <td>{formatAverageMissionTime(player.avg_duration_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : activeTab === 'explorers' ? (
          <table className="honor-table explorers-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th title={t('exploreUnknownRanking.columnHint')}>
                  {t('honorHall.missionReached')}
                </th>
                <th>{t('honorHall.strategy')}</th>
                <th>{t('honorHall.action')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((player, index) => (
                <tr
                  key={player.username}
                  className={showUserRank && player.username === user?.username ? 'highlighted' : ''}
                >
                  <td>{startIndex + index + 1}</td>
                  <td>{player.username}</td>
                  <td>
                    <ExploreUnknownMissionDepthDisplay
                      missions={player.missions_completed}
                      easyCount={player.easy_missions_completed}
                      showCellHint
                    />
                  </td>
                  <td>{player.run_strategy_name != null && player.run_strategy_name !== '' ? player.run_strategy_name : '-'}</td>
                  <td>
                    {player.subscribe_strategy_id == null ? (
                      <span className="explorers-no-subscribe">-</span>
                    ) : (
                      renderStrategyAction(
                        player.subscribe_strategy_id,
                        player.is_owned,
                        player.is_subscribed
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : isPlayerTab ? (
          <table className="honor-table battle-legends-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th>{t('honorHall.winRate')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((player, index) => (
                <tr 
                  key={player.username}
                  className={showUserRank && player.username === user?.username ? 'highlighted' : ''}
                >
                  <td>{startIndex + index + 1}</td>
                  <td>{player.username}</td>
                  <td>{player.win_rate || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="honor-table strategy-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th>{t('honorHall.strategy')}</th>
                <th>{t('honorHall.winRate')}</th>
                <th>{t('honorHall.action')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((strategy, index) => (
                <tr key={`${strategy.strategy_id}-${strategy.player_id}`}>
                  <td>{startIndex + index + 1}</td>
                  <td>{strategy.player_name}</td>
                  <td>{strategy.strategy_name}</td>
                  <td>{strategy.win_rate}%</td>
                  <td>
                    {renderStrategyAction(
                      strategy.strategy_id,
                      strategy.is_owned,
                      strategy.is_subscribed
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="pagination">
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setCurrentPage(prev => Math.max(1, prev - 1));
          }}
          disabled={currentPage === 1}
        >
          {t('honorHall.previous')}
        </button>
        <span>{t('honorHall.page')} {currentPage} {t('honorHall.of')} {totalPages}</span>
        <button 
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setCurrentPage(prev => Math.min(totalPages, prev + 1));
          }}
          disabled={currentPage === totalPages}
        >
          {t('honorHall.next')}
        </button>
      </div>

      <TutorialReplayButton user={user} onReplay={() => setShowTutorialHonorHall(true)} />

      {showTutorialHonorHall && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_salute.png"
          text={t('honorHall.tutorialText')}
          onContinue={async () => {
            if (!isReplayingHonorHallTutorial) {
              const token = localStorage.getItem('token');
              if (token) {
                await fetch(`${API_URL}/tutorial/mark-seen`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ messageKey: 'honor_hall' }),
                });
                markSeenLocally('honor_hall');
              }
            }
            setShowTutorialHonorHall(false);
          }}
          voiceKey="honorHall.tutorialText"
        />,
        document.body
      )}
        </div>
      </div>
    </div>
  );
}

export default HonorHall;
